import { type Range, useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useMemo } from 'react'
import type { DataTableColumnVirtualizer, DataTableInstance, DataTableRowData } from '../types'
import { parseFromValuesOrFunc } from '../utils/utils'
import { extraIndexRangeExtractor } from '../utils/virtualization.utils'

export const useTableColumnVirtualizer = <
  TData extends DataTableRowData,
  TScrollElement extends Element | Window = HTMLDivElement,
  TItemElement extends Element = HTMLTableCellElement,
>(
  table: DataTableInstance<TData>,
): DataTableColumnVirtualizer | undefined => {
  const {
    getLeftLeafColumns,
    getRightLeafColumns,
    getState,
    getVisibleLeafColumns,
    options: {
      columnVirtualizerInstanceRef,
      columnVirtualizerOptions,
      enableColumnPinning,
      enableColumnVirtualization,
    },
    refs: { tableContainerRef },
  } = table
  const { columnPinning, draggingColumn } = getState()

  if (!enableColumnVirtualization) return undefined

  const columnVirtualizerProps = parseFromValuesOrFunc(columnVirtualizerOptions, {
    table,
  })

  const visibleColumns = getVisibleLeafColumns()

  const [leftPinnedIndexes, rightPinnedIndexes] = useMemo(
    () =>
      enableColumnPinning
        ? [
            getLeftLeafColumns().map((c) => c.getPinnedIndex()),
            getRightLeafColumns()
              .map((column) => visibleColumns.length - column.getPinnedIndex() - 1)
              .sort((a, b) => a - b),
          ]
        : [[], []],
    [visibleColumns.length, columnPinning, enableColumnPinning],
  )

  const numPinnedLeft = leftPinnedIndexes.length
  const numPinnedRight = rightPinnedIndexes.length

  const draggingColumnIndex = useMemo(
    () =>
      draggingColumn?.id ? visibleColumns.findIndex((c) => c.id === draggingColumn?.id) : undefined,
    [draggingColumn?.id],
  )

  const columnVirtualizer = useVirtualizer({
    count: visibleColumns.length,
    estimateSize: (index) => visibleColumns[index].getSize(),
    getScrollElement: () => tableContainerRef.current,
    horizontal: true,
    overscan: 3,
    rangeExtractor: useCallback(
      (range: Range) => {
        const newIndexes = extraIndexRangeExtractor(range, draggingColumnIndex)
        if (!numPinnedLeft && !numPinnedRight) {
          return newIndexes
        }
        return [...new Set([...leftPinnedIndexes, ...newIndexes, ...rightPinnedIndexes])]
      },
      [leftPinnedIndexes, rightPinnedIndexes, draggingColumnIndex],
    ),
    ...columnVirtualizerProps,
  }) as unknown as DataTableColumnVirtualizer<TScrollElement, TItemElement>

  const virtualColumns = columnVirtualizer.getVirtualItems()
  // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
  columnVirtualizer.virtualColumns = virtualColumns as any
  const numColumns = virtualColumns.length

  if (numColumns) {
    const totalSize = columnVirtualizer.getTotalSize()

    const leftNonPinnedStart = virtualColumns[numPinnedLeft]?.start || 0
    const leftNonPinnedEnd = virtualColumns[leftPinnedIndexes.length - 1]?.end || 0

    const rightNonPinnedStart = virtualColumns[numColumns - numPinnedRight]?.start || 0
    const rightNonPinnedEnd = virtualColumns[numColumns - numPinnedRight - 1]?.end || 0

    columnVirtualizer.virtualPaddingLeft = leftNonPinnedStart - leftNonPinnedEnd

    columnVirtualizer.virtualPaddingRight =
      totalSize - rightNonPinnedEnd - (numPinnedRight ? totalSize - rightNonPinnedStart : 0)
  }

  if (columnVirtualizerInstanceRef) {
    // @ts-expect-error virtualiseRef
    columnVirtualizerInstanceRef.current = columnVirtualizer
  }

  // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
  return columnVirtualizer as any
}
